import { RETURN_METHOD_COMPLAINT, RETURN_METHOD_EXCHANGE, RETURN_METHOD_RETURN } from "../constants";
import { isReturnTimeExceeded } from "./appCore";
import { isPriceListComplaintsDisabled, isPriceListExchangesComplaintDisabled, isPriceListExchangesDisabled } from "./brandDataCore";
import { PRODUCT_POLICIES } from "rylak-common/src/constants";
import { getVariantsWithInventory } from "../cores/exchangeToProductsCore.js";
import { getComplaintSubReason } from "backoffice/src/cores/returnReasonCore.js";

/** Products (multiple) **/
export function shouldBeTwoProductRows(products) {
    return products?.length > 4;
}

export function shouldBeThreeProductRows(products) {
    return products?.length > 8;
}

/** Product **/

export function getActualProductId(p) {
    return p?.actualProductId;
}

export function getProductName(p) {
    return p?.productName || p?.title;
}

export function getProductImageSrc(p) {
    return getProductImages(p)?.[0] || p?.imageSrc || p?.imageUrl;
}

export function getProductImages(p) {
    if (p?.imageSrcs?.length > 0) {
        return p?.imageSrcs?.slice(0, 10);
    }
    const image = p?.imageSrc || p?.imageUrl;
    if (image) {
        return [image];
    }
    return [];
}

export function getProductProperties(p) {
    return p?.properties;
}

export function getProductPolicy(p) {
    return p?.productPolicy;
}

export function isProductComplaintDisabled(p, { appProps }) {
    return getProductPolicy(p)?.complaintPolicy === PRODUCT_POLICIES.ALWAYS_REJECT || isPriceListComplaintsDisabled(appProps);
}

export function isReturnDisabled(p, { appProps }) {
    return isReturnTimeExceeded(appProps) || getProductPolicy(p)?.returnPolicy === PRODUCT_POLICIES.ALWAYS_REJECT || p?.returnable === false;
}

export function isProductExchangeDisabled(p, { appProps }) {
    return isReturnTimeExceeded(appProps) || getProductPolicy(p)?.exchangePolicy === PRODUCT_POLICIES.ALWAYS_REJECT || isPriceListExchangesDisabled(appProps);
}

export function isProductExchangeComplaintDisabled(p, { appProps }) {
    return getProductPolicy(p)?.exchangePolicy === PRODUCT_POLICIES.ALWAYS_REJECT || isPriceListExchangesComplaintDisabled(appProps);
}

export function canSelectProductExchangeToAlternatives(p, { appProps, productExchangeServiceData }) {
    return getVariantsWithInventory(productExchangeServiceData)?.length > 0 && !isProductExchangeComplaintDisabled(p, { appProps });
}

export function isProductMethodSelectorEnabled(p, { appProps }) {
    return !(isReturnDisabled(p, { appProps }) && isProductExchangeDisabled(p, { appProps }) && isProductComplaintDisabled(p, { appProps }));
}

export function isOnlyProductReturnEnabled(p, { appProps }) {
    return (
        isReturnMethodDisabled(p, { returnMethod: RETURN_METHOD_EXCHANGE, appProps }) &&
        isReturnMethodDisabled(p, { returnMethod: RETURN_METHOD_COMPLAINT, appProps }) &&
        !isReturnMethodDisabled(p, { returnMethod: RETURN_METHOD_RETURN, appProps })
    );
}

export function isReturnMethodDisabled(p, { returnMethod, appProps }) {
    if (returnMethod === RETURN_METHOD_COMPLAINT) {
        return isProductComplaintDisabled(p, { appProps });
    }
    if (returnMethod === RETURN_METHOD_RETURN) {
        return isReturnDisabled(p, { appProps });
    }
    if (returnMethod === RETURN_METHOD_EXCHANGE) {
        return isProductExchangeDisabled(p, { appProps });
    }
    return false;
}

export function getProductPropertiesAsString(p) {
    return getProductProperties(p)
        ?.map((property) => {
            return property.value;
        })
        .join(", ");
}

export function getProductComplaintReasons(p) {
    const complaintReasonsInConfig = p?.returnReasonConfig?.complaintReasons;
    const oldComplaintReasons = p?.complaintReasons; // Backwards compatibility
    return complaintReasonsInConfig?.length > 0 ? complaintReasonsInConfig : oldComplaintReasons;
}

export function getProductComplaintReason(p, { complaintReasonId }) {
    return getProductComplaintReasons(p)?.find((complaint) => complaint?.id === complaintReasonId);
}

export function getSelectedProductComplaintReasonFromReasonId(p, { complaintReasonId, complaintSubReasonId }) {
    const complaintReason = getProductComplaintReason(p, { complaintReasonId });
    if (complaintSubReasonId) {
        return getComplaintSubReason(complaintReason, { complaintSubReasonId });
    } else {
        return complaintReason;
    }
}
